import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import type {
  Playbook,
  PlaybookFrequency,
  PlaybookInstance,
  PlaybookInstanceStatus,
  PlaybookInstanceWithMeta,
  PlaybookStep,
  PlaybookWithSteps,
} from './types';

interface PlaybookRowJoin extends Playbook {
  steps?: PlaybookStep[];
}

export function usePlaybooks() {
  return useQuery({
    queryKey: ['playbooks'],
    staleTime: 60_000,
    queryFn: async (): Promise<PlaybookWithSteps[]> => {
      const { data, error } = await supabase
        .from('playbooks')
        .select('*, steps:playbook_steps(*)')
        .is('deleted_at', null)
        .order('frequency', { ascending: true })
        .order('name', { ascending: true });
      if (error) throw error;
      return (data as PlaybookRowJoin[]).map((p) => ({
        ...p,
        steps: (p.steps ?? []).slice().sort((a, b) => a.order_index - b.order_index),
      }));
    },
  });
}

interface InstanceRowJoin extends PlaybookInstance {
  playbook?: { name: string; frequency: PlaybookFrequency } | null;
  client?: { name: string | null } | null;
}

interface TaskCountRow {
  source_ref: { instance_id?: string } | null;
  status: string;
}

export function usePlaybookInstances(filters?: {
  clientId?: string;
  status?: PlaybookInstanceStatus;
}) {
  return useQuery({
    queryKey: ['playbook_instances', filters],
    staleTime: 30_000,
    queryFn: async (): Promise<PlaybookInstanceWithMeta[]> => {
      let q = supabase
        .from('playbook_instances')
        .select('*, playbook:playbooks(name, frequency), client:clients(name)')
        .order('period_start', { ascending: false })
        .limit(200);

      if (filters?.clientId) q = q.eq('client_id', filters.clientId);
      if (filters?.status) q = q.eq('status', filters.status);

      const { data: instances, error } = await q;
      if (error) throw error;

      const instanceIds = (instances as InstanceRowJoin[]).map((i) => i.id);
      if (instanceIds.length === 0) return [];

      // Conta tasks por instance via source_ref
      const { data: tasks, error: tasksErr } = await supabase
        .from('tasks')
        .select('source_ref, status')
        .eq('source', 'playbook')
        .is('deleted_at', null)
        .in('source_ref->>instance_id', instanceIds);
      if (tasksErr) throw tasksErr;

      const counts = new Map<string, { total: number; completed: number }>();
      for (const t of (tasks ?? []) as TaskCountRow[]) {
        const id = t.source_ref?.instance_id;
        if (!id) continue;
        const cur = counts.get(id) ?? { total: 0, completed: 0 };
        cur.total += 1;
        if (t.status === 'completed') cur.completed += 1;
        counts.set(id, cur);
      }

      return (instances as InstanceRowJoin[]).map((row) => {
        const c = counts.get(row.id) ?? { total: 0, completed: 0 };
        return {
          ...row,
          playbook_name: row.playbook?.name ?? '—',
          playbook_frequency: row.playbook?.frequency ?? 'daily',
          client_name: row.client?.name ?? null,
          task_total: c.total,
          task_completed: c.completed,
        };
      });
    },
  });
}

export function useRunGenerateInstances() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (targetDate?: string) => {
      const { data, error } = await supabase.rpc('generate_playbook_instances', {
        p_target_date: targetDate ?? new Date().toISOString().slice(0, 10),
      });
      if (error) throw error;
      return data as { instances_created: number; tasks_created: number; target_date: string };
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['playbook_instances'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.success(
        result.instances_created > 0
          ? `${result.instances_created} instância(s) e ${result.tasks_created} tarefa(s) criadas`
          : 'Nenhuma nova instância (já existiam ou não há frequência hoje)',
      );
    },
    onError: (e: Error) => toast.error('Erro ao gerar instâncias: ' + e.message),
  });
}
